import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import { supabase, isSupabaseConfigured } from '../../lib/supabase';
import { MOCK_ADDONS } from '../../lib/mockData';

const EMPTY_FORM = {
  name: '',
  description: '',
  price: '',
  image: '',
  available: true,
  sort_order: 0,
  max_quantity: '5',
};

// Convert DB row to form shape
const dbToForm = (row) => ({
  ...row,
  description: row.description || '',
  image: row.image || row.image_url || '',
  price: row.price != null ? String(row.price) : '',
  max_quantity: row.max_quantity != null ? String(row.max_quantity) : '5',
});

// Convert form shape back to DB shape for upsert
const formToDb = (form) => ({
  name: form.name,
  description: form.description,
  price: Number(form.price),
  image_url: form.image,
  image: form.image,
  available: form.available,
  sort_order: Number(form.sort_order) || 0,
  max_quantity: Number(form.max_quantity) || 5,
});

function AddonFormPanel({ initial, onSave, onCancel, saving }) {
  const [form, setForm] = useState(initial ? dbToForm(initial) : EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const nameRef = useRef();

  useEffect(() => {
    setForm(initial ? dbToForm(initial) : EMPTY_FORM);
    setErrors({});
    setTimeout(() => nameRef.current?.focus(), 50);
  }, [initial]);

  const set = (key, val) => {
    setForm(prev => ({ ...prev, [key]: val }));
    if (errors[key]) setErrors(prev => ({ ...prev, [key]: '' }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Name is required';
    if (!form.price || isNaN(Number(form.price)) || Number(form.price) < 0)
      e.price = 'Enter a valid price';
    return e;
  };

  const handleSubmit = () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    onSave(formToDb(form));
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      className="bg-white rounded-2xl border border-blush-100 shadow-soft p-6 space-y-5"
    >
      <h3 className="font-serif text-xl text-charcoal">
        {initial?.id ? 'Edit Add-on' : 'Add New Add-on'}
      </h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

        {/* Name */}
        <div className="md:col-span-2">
          <label className="block text-xs font-bold text-sage-500 uppercase tracking-wider mb-1.5">
            Add-on Name *
          </label>
          <input
            ref={nameRef}
            type="text"
            value={form.name}
            onChange={e => set('name', e.target.value)}
            placeholder="e.g. Garlic Sauce"
            className={`input-field ${errors.name ? 'border-red-300' : ''}`}
          />
          {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name}</p>}
        </div>

        {/* Description */}
        <div className="md:col-span-2">
          <label className="block text-xs font-bold text-sage-500 uppercase tracking-wider mb-1.5">
            Description
          </label>
          <input
            type="text"
            value={form.description}
            onChange={e => set('description', e.target.value)}
            placeholder="Short description shown under the add-on card..."
            className="input-field"
          />
        </div>

        {/* Price */}
        <div>
          <label className="block text-xs font-bold text-sage-500 uppercase tracking-wider mb-1.5">
            Price ($) *
          </label>
          <input
            type="number"
            step="0.01"
            min="0"
            value={form.price}
            onChange={e => set('price', e.target.value)}
            placeholder="1.50"
            className={`input-field ${errors.price ? 'border-red-300' : ''}`}
          />
          {errors.price && <p className="text-xs text-red-500 mt-1">{errors.price}</p>}
        </div>

        {/* Sort order */}
        <div>
          <label className="block text-xs font-bold text-sage-500 uppercase tracking-wider mb-1.5">
            Display Order
          </label>
          <input
            type="number"
            min="0"
            value={form.sort_order}
            onChange={e => set('sort_order', e.target.value)}
            placeholder="0"
            className="input-field"
          />
          <p className="text-[10px] text-sage-400 mt-1">Lower number = shown first</p>
        </div>

        {/* Max Quantity */}
        <div>
          <label className="block text-xs font-bold text-sage-500 uppercase tracking-wider mb-1.5">
            Max Quantity Per Order
          </label>
          <input
            type="number"
            min="1"
            value={form.max_quantity}
            onChange={e => set('max_quantity', e.target.value)}
            placeholder="5"
            className="input-field"
          />
          <p className="text-[10px] text-sage-400 mt-1">Maximum quantity a customer can order</p>
        </div>

        {/* Image URL */}
        <div className="md:col-span-2">
          <label className="block text-xs font-bold text-sage-500 uppercase tracking-wider mb-1.5">
            Image URL
          </label>
          <input
            type="url"
            value={form.image}
            onChange={e => set('image', e.target.value)}
            placeholder="https://... or /images/garlic-sauce.png"
            className="input-field"
          />
          {form.image && (
            <div className="mt-2 w-16 h-16 rounded-xl overflow-hidden border border-sage-100 bg-sage-50 flex items-center justify-center">
              <img
                src={form.image}
                alt="preview"
                className="w-full h-full object-contain p-1"
                onError={e => { e.target.style.display = 'none'; }}
              />
            </div>
          )}
        </div>

        {/* Available toggle */}
        <div className="md:col-span-2 flex items-center gap-3">
          <button
            type="button"
            onClick={() => set('available', !form.available)}
            className={`relative w-12 h-6 rounded-full transition-colors duration-200 ${
              form.available ? 'bg-green-400' : 'bg-sage-200'
            }`}
          >
            <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200 ${
              form.available ? 'translate-x-6' : 'translate-x-0'
            }`} />
          </button>
          <span className="text-sm font-medium text-charcoal">
            {form.available ? 'Available — shown to customers' : 'Hidden — not shown to customers'}
          </span>
        </div>
      </div>

      <div className="flex gap-3 pt-2">
        <button
          onClick={handleSubmit}
          disabled={saving}
          className="btn-primary flex items-center gap-2"
        >
          {saving
            ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Saving...</>
            : <><SafeIcon name="Check" /> {initial?.id ? 'Save Changes' : 'Add Add-on'}</>
          }
        </button>
        <button onClick={onCancel} disabled={saving} className="btn-secondary">
          Cancel
        </button>
      </div>
    </motion.div>
  );
}

function AddonRow({ addon, onEdit, onToggle, onDelete, onMoveUp, onMoveDown, isFirst, isLast, deleting }) {
  return (
    <motion.div
      layout
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className={`flex items-center gap-4 bg-white rounded-2xl border border-sage-100 p-4 shadow-soft transition-opacity ${
        !addon.available ? 'opacity-60' : ''
      }`}
    >
      {/* Image */}
      <div className="w-14 h-14 rounded-xl bg-sage-50 border border-sage-100 flex items-center justify-center overflow-hidden shrink-0">
        {addon.image || addon.image_url ? (
          <img
            src={addon.image || addon.image_url}
            alt={addon.name}
            className="w-full h-full object-contain p-1"
            onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'block'; }}
          />
        ) : null}
        <span className="text-2xl hidden">🍽️</span>
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-bold text-charcoal text-sm">{addon.name}</span>
          <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${
            addon.available
              ? 'bg-green-100 text-green-700'
              : 'bg-red-100 text-red-600'
          }`}>
            {addon.available ? 'On' : 'Off'}
          </span>
        </div>
        <p className="text-xs text-sage-400 mt-0.5 truncate">{addon.description}</p>
      </div>

      {/* Price & Limit */}
      <div className="flex flex-col items-end shrink-0">
        <span className="font-bold text-blush-500">${Number(addon.price).toFixed(2)}</span>
        <span className="text-[10px] text-sage-400">Max: {addon.max_quantity || 5}</span>
      </div>

      {/* Sort controls */}
      <div className="flex flex-col gap-0.5 shrink-0">
        <button
          onClick={() => onMoveUp(addon)}
          disabled={isFirst}
          className="w-6 h-6 rounded-lg bg-sage-50 flex items-center justify-center text-sage-400 hover:text-charcoal disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        >
          <SafeIcon name="ChevronUp" />
        </button>
        <button
          onClick={() => onMoveDown(addon)}
          disabled={isLast}
          className="w-6 h-6 rounded-lg bg-sage-50 flex items-center justify-center text-sage-400 hover:text-charcoal disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        >
          <SafeIcon name="ChevronDown" />
        </button>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 shrink-0">
        <button
          onClick={() => onEdit(addon)}
          className="w-8 h-8 rounded-xl bg-sage-50 flex items-center justify-center text-sage-500 hover:bg-sage-100 transition-colors"
          title="Edit"
        >
          <SafeIcon name="Pencil" />
        </button>
        <button
          onClick={() => onToggle(addon)}
          className={`w-8 h-8 rounded-xl flex items-center justify-center transition-colors ${
            addon.available
              ? 'bg-amber-50 text-amber-500 hover:bg-amber-100'
              : 'bg-green-50 text-green-500 hover:bg-green-100'
          }`}
          title={addon.available ? 'Hide' : 'Show'}
        >
          <SafeIcon name={addon.available ? 'EyeOff' : 'Eye'} />
        </button>
        <button
          onClick={() => onDelete(addon)}
          disabled={deleting === addon.id}
          className="w-8 h-8 rounded-xl bg-red-50 flex items-center justify-center text-red-400 hover:bg-red-100 transition-colors disabled:opacity-50"
          title="Delete"
        >
          {deleting === addon.id
            ? <div className="w-3 h-3 border-2 border-red-300 border-t-transparent rounded-full animate-spin" />
            : <SafeIcon name="Trash2" />
          }
        </button>
      </div>
    </motion.div>
  );
}

export default function AddonsPanel() {
  const [addons, setAddons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingAddon, setEditingAddon] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [toast, setToast] = useState('');

  useEffect(() => { fetchAddons(); }, []);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3000);
  };

  async function fetchAddons() {
    setLoading(true);
    if (isSupabaseConfigured()) {
      const { data, error } = await supabase
        .from('addons')
        .select('*')
        .order('sort_order');
      setAddons(error ? MOCK_ADDONS : (data || MOCK_ADDONS));
    } else {
      setAddons(MOCK_ADDONS);
    }
    setLoading(false);
  }

  const handleSave = async (dbData) => {
    setSaving(true);
    try {
      if (isSupabaseConfigured()) {
        if (editingAddon?.id && !String(editingAddon.id).startsWith('mock')) {
          const { error } = await supabase
            .from('addons')
            .update(dbData)
            .eq('id', editingAddon.id);
          if (error) throw error;
          showToast('Add-on updated ✓');
        } else {
          const { error } = await supabase
            .from('addons')
            .insert(dbData);
          if (error) throw error;
          showToast('Add-on added ✓');
        }
        await fetchAddons();
      } else {
        if (editingAddon?.id) {
          setAddons(prev => prev.map(a => a.id === editingAddon.id ? { ...a, ...dbData } : a));
        } else {
          setAddons(prev => [...prev, { ...dbData, id: `mock-${Date.now()}` }]);
        }
        showToast('Saved (mock mode) ✓');
      }
      setShowForm(false);
      setEditingAddon(null);
    } catch (err) {
      alert('Failed to save: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (addon) => {
    setEditingAddon(addon);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleToggle = async (addon) => {
    const updated = { ...addon, available: !addon.available };
    setAddons(prev => prev.map(a => a.id === addon.id ? updated : a));
    if (isSupabaseConfigured()) {
      await supabase.from('addons').update({ available: updated.available }).eq('id', addon.id);
    }
    showToast(updated.available ? 'Add-on is now visible ✓' : 'Add-on hidden ✓');
  };

  const handleDelete = async (addon) => {
    if (!window.confirm(`Delete "${addon.name}"? This cannot be undone.`)) return;
    setDeleting(addon.id);
    try {
      if (isSupabaseConfigured()) {
        await supabase.from('addons').delete().eq('id', addon.id);
      }
      setAddons(prev => prev.filter(a => a.id !== addon.id));
      showToast('Add-on deleted ✓');
    } catch (err) {
      alert('Delete failed: ' + err.message);
    } finally {
      setDeleting(null);
    }
  };

  const handleMoveUp = async (addon) => {
    const idx = addons.findIndex(a => a.id === addon.id);
    if (idx === 0) return;
    const reordered = [...addons];
    [reordered[idx - 1], reordered[idx]] = [reordered[idx], reordered[idx - 1]];
    const updated = reordered.map((a, i) => ({ ...a, sort_order: i }));
    setAddons(updated);
    if (isSupabaseConfigured()) {
      await Promise.all(updated.map(a =>
        supabase.from('addons').update({ sort_order: a.sort_order }).eq('id', a.id)
      ));
    }
  };

  const handleMoveDown = async (addon) => {
    const idx = addons.findIndex(a => a.id === addon.id);
    if (idx === addons.length - 1) return;
    const reordered = [...addons];
    [reordered[idx], reordered[idx + 1]] = [reordered[idx + 1], reordered[idx]];
    const updated = reordered.map((a, i) => ({ ...a, sort_order: i }));
    setAddons(updated);
    if (isSupabaseConfigured()) {
      await Promise.all(updated.map(a =>
        supabase.from('addons').update({ sort_order: a.sort_order }).eq('id', a.id)
      ));
    }
  };

  return (
    <div className="space-y-6">

      {/* Toast */}
      <AnimatePresence>
        {toast && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="fixed top-6 right-6 z-50 bg-green-600 text-white px-5 py-3 rounded-2xl shadow-lg font-bold text-sm flex items-center gap-2"
          >
            <SafeIcon name="CheckCircle" /> {toast}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-serif text-2xl text-charcoal">Add-ons</h2>
          <p className="text-sage-500 text-sm mt-0.5">
            {addons.filter(a => a.available).length} available · {addons.length} total
          </p>
        </div>
        {!showForm && (
          <button
            onClick={() => { setEditingAddon(null); setShowForm(true); }}
            className="btn-primary flex items-center gap-2"
          >
            <SafeIcon name="Plus" /> Add Add-on
          </button>
        )}
      </div>

      {/* Form */}
      <AnimatePresence>
        {showForm && (
          <AddonFormPanel
            initial={editingAddon}
            onSave={handleSave}
            onCancel={() => { setShowForm(false); setEditingAddon(null); }}
            saving={saving}
          />
        )}
      </AnimatePresence>

      {/* List */}
      {loading ? (
        <div className="flex justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blush-300" />
        </div>
      ) : addons.length === 0 ? (
        <div className="bg-white rounded-2xl border border-sage-100 p-12 text-center space-y-3">
          <div className="text-5xl">🧄</div>
          <p className="font-serif text-xl text-charcoal">No add-ons yet</p>
          <p className="text-sage-400 text-sm">Add sauces, sides and extras for customers to choose from.</p>
          <button
            onClick={() => { setEditingAddon(null); setShowForm(true); }}
            className="btn-primary inline-flex items-center gap-2 mt-2"
          >
            <SafeIcon name="Plus" /> Add First Add-on
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          <AnimatePresence>
            {addons.map((addon, idx) => (
              <AddonRow
                key={addon.id}
                addon={addon}
                onEdit={handleEdit}
                onToggle={handleToggle}
                onDelete={handleDelete}
                onMoveUp={handleMoveUp}
                onMoveDown={handleMoveDown}
                isFirst={idx === 0}
                isLast={idx === addons.length - 1}
                deleting={deleting}
              />
            ))}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
}
